import { useAuth } from '../hooks/useAuth'
import { useState, useEffect } from 'react'
import { fetchRows, fetchCrewDb } from '../lib/api'
import FleetMap, { fmtLat, fmtLon, ago } from '../components/FleetMap'
import FleetCalendar from '../components/FleetCalendar'
import CrewLogins from './CrewLogins'
import ApprovalsCard, { rememberOpen } from '../components/ApprovalsCard'
import { supabase } from '../lib/supabase'
import { featureOn } from '../lib/features'
import { drillsNeedingAttention } from '../lib/drillTypes'
import { vesselHealth, worstFirst, TONE_COLOR } from '../lib/fleetCore'
import { expiryStatus, expiryToday } from '../lib/expiry'
import { vesselCompliance } from '../lib/fleetCompliance'
import FleetCompliance from '../components/FleetCompliance'
import FleetReportButton from '../components/FleetReportButton'
import { tutorialMark } from '../lib/tutorialStore'
import FleetStandards from '../components/FleetStandards'
import FleetTeam from '../components/FleetTeam'
import { memberCan, cleanRights } from '../lib/companyMembers'
import { trendFrom, arrowOf, sparkPath } from '../lib/readinessTrend'

function getVal(v) {
  if (!v) return ''
  if (typeof v === 'object' && !Array.isArray(v)) return v.value || v.name || v.label || ''
  if (Array.isArray(v)) return v.map(getVal).filter(Boolean).join(', ')
  return String(v)
}
const NAME_KEYS = ['Task Name', 'Task', 'Title', 'Document Name', 'Document', 'Certificate', 'Name', 'Equipment Name', 'Equipment', 'Item Name', 'Item', 'Description']
function nameOf(r) {
  for (const k of NAME_KEYS) { const v = getVal(r[k]); if (v) return v }
  for (const k of Object.keys(r)) { if (/name|title|task|item|equipment|document|certif|descr/i.test(k)) { const v = getVal(r[k]); if (v) return v } }
  return 'Untitled'
}
function dateOf(r, re) {
  for (const k of Object.keys(r)) { if (re.test(k)) { const v = getVal(r[k]); if (v && !isNaN(new Date(v))) return v } }
  return ''
}
function fmtDate(v) {
  if (!v) return ''
  const d = new Date(v)
  if (isNaN(d)) return v
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
const tsOf = v => { const d = new Date(v); return isNaN(d) ? Infinity : d.getTime() }
const localToday = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') }

const C = { card: '#101c31', line: 'rgba(255,255,255,.07)', text: '#e7edf6', muted: '#8fa1bb', faint: '#5d6f8a', blue: '#3b7bff', green: '#35c98b', red: '#e5484d', amber: '#e0a23a', purple: '#9b7bff' }
const GROUP_PREVIEW = 3

const CSS = `
.hcf{color:${C.text};max-width:1500px;margin:0 auto;padding:4px 4px 40px}
.hcf-card{background:${C.card};border:1px solid ${C.line};border-radius:14px;padding:18px;min-width:0}
.hcf-kpis{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:14px;margin-bottom:16px}
@media (max-width:1679px){.hcf-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}}
.hcf-row2{display:grid;grid-template-columns:minmax(0,1.2fr) minmax(0,1.1fr) minmax(0,.8fr);gap:14px;margin-bottom:16px}
.hcf-row3{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px}
.hcf-h{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;gap:10px}
.hcf-h b{font-size:15px;font-weight:700}
.hcf-h span{font-size:11.5px;color:${C.muted}}
.hcf-tr{display:grid;align-items:center;gap:10px;padding:9px 8px;border-top:1px solid ${C.line};cursor:pointer;border-radius:8px;transition:background .12s}
.hcf-tr:hover,.hcf-tr.hl{background:rgba(59,123,255,.08)}
.hcf-tr.sel{background:rgba(59,123,255,.14);box-shadow:inset 3px 0 0 ${C.blue}}
.hcf-th{display:grid;gap:10px;padding:0 8px 8px;font-size:11px;color:${C.muted};font-weight:600}
.hcf-pill{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:600;padding:3px 10px;border-radius:12px;white-space:nowrap}
.hcf-pill i{width:6px;height:6px;border-radius:50%;display:inline-block}
.hcf-sub{font-size:11.5px;color:${C.muted};margin-top:2px}
.hcf-ell{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.hcf-scroll{max-height:360px;overflow-y:auto;padding-right:2px}
.hcf-chips{display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;margin-bottom:16px;align-items:center}
.hcf-chip{display:inline-flex;align-items:center;gap:8px;padding:7px 14px;border-radius:20px;border:1px solid ${C.line};background:${C.card};color:${C.text};font:600 12.5px/1 inherit;cursor:pointer;white-space:nowrap;flex-shrink:0;font-family:inherit}
.hcf-chip:hover{border-color:rgba(59,123,255,.5)}
.hcf-chip.on{background:${C.blue};border-color:${C.blue};color:#fff}
.hcf-chip em{font-style:normal;font-size:10.5px;font-weight:700;padding:2px 7px;border-radius:10px;background:rgba(229,72,77,.2);color:#ff8a8d}
.hcf-chip.on em{background:rgba(255,255,255,.22);color:#fff}
.hcf-gh{display:flex;align-items:center;gap:8px;padding:10px 8px 6px;margin-top:4px;font-size:12px;font-weight:700;color:${C.text};cursor:pointer;border-top:1px solid ${C.line}}
.hcf-gh:hover span{color:${C.blue}}
.hcf-gh em{font-style:normal;font-size:10.5px;font-weight:700;padding:1px 7px;border-radius:10px;background:rgba(143,161,187,.16);color:${C.muted}}
.hcf-more{display:block;padding:6px 8px 8px 48px;font-size:11.5px;color:${C.blue};font-weight:600;cursor:pointer}
.hcf-more:hover{text-decoration:underline}
.hcf-num{font-size:13px;font-weight:700;text-align:center;font-variant-numeric:tabular-nums}
.hcf-open{background:transparent;border:1px solid ${C.line};color:${C.muted};border-radius:8px;width:30px;height:28px;cursor:pointer;font-size:14px;font-family:inherit}
.hcf-open:hover{border-color:${C.blue};color:#fff;background:${C.blue}}
.hcf-ov{border-radius:10px;margin-top:6px;border-top:none;background:rgba(255,255,255,.02)}
.hcf-ov+.hcf-ov{margin-top:6px}
.hcf-vc{display:grid;grid-template-columns:38px minmax(0,1fr);gap:10px;align-items:center;min-width:0}
.hcf-vn{font-size:13.5px;font-weight:600;line-height:1.25;overflow-wrap:anywhere}
.hcf-why{grid-column:1/-1;font-size:11.5px;font-weight:600;margin:-2px 0 0 48px;line-height:1.35}
.hcf-ring{position:relative;width:38px;height:38px;flex-shrink:0}
.hcf-ring b{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;font-variant-numeric:tabular-nums}
.hcf-win{font-size:10px;font-weight:700;padding:1px 6px;border-radius:8px;margin-left:6px;white-space:nowrap}
.hcf-brief{display:inline-flex;align-items:center;gap:7px;background:rgba(59,123,255,.14);border:1px solid rgba(59,123,255,.5);color:#cfe0ff;border-radius:10px;padding:8px 14px;font:600 12.5px/1 inherit;font-family:inherit;cursor:pointer}
.hcf-brief:hover{background:${C.blue};color:#fff}
@media (max-width:1280px){.hcf-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}.hcf-row2{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}.hcf-row2>:first-child{grid-column:1/-1}}
@media (max-width:1023px){.hcf-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.hcf-row2,.hcf-row3{grid-template-columns:minmax(0,1fr)}.hcf-scroll.hcf-all{max-height:none;overflow:visible}}
.hcf-trend{display:flex;align-items:center;gap:6px;margin-top:4px;font-size:10.5px;font-weight:700;font-variant-numeric:tabular-nums;color:${C.muted};flex-wrap:wrap}
.hcf-trend svg{flex-shrink:0;display:block}
.hcf-tr-chip{display:inline-flex;align-items:center;gap:3px;padding:1px 6px;border-radius:8px;background:rgba(143,161,187,.12);white-space:nowrap}
.hcf-tr-chip.up{background:rgba(53,201,139,.14);color:${C.green}}.hcf-tr-chip.down{background:rgba(229,72,77,.14);color:#ff8a8d}
.hcf-th i{font-style:normal}.hcf-short{display:none}
@media (max-width:600px){.hcf-long{display:none}.hcf-short{display:inline}.hcf-ov,.hcf-th-ov{grid-template-columns:minmax(0,1fr) 30px 30px 28px 28px!important;gap:6px!important}.hcf-ov{padding:9px 6px}}
`

const ICONS = {
  ship: <path d="M3 15h18l-2.6 4.5H5.6zM6 15l1.5-4h8.5l2 4M9.5 11V8h4l1 3" />,
  pin: <><path d="M12 21s-6.8-5.8-6.8-10.7a6.8 6.8 0 0113.6 0C18.8 15.2 12 21 12 21z" /><circle cx="12" cy="10" r="2.4" /></>,
  wrench: <path d="M14.6 6.3a3.7 3.7 0 00-4.9 4.4l-5.2 5.2a1.8 1.8 0 002.6 2.6l5.2-5.2a3.7 3.7 0 004.4-4.9l-2.5 2.5-2-.1-.1-2z" />,
  alert: <><path d="M12 4L2.8 19.5h18.4z" /><path d="M12 10.2v4" /><circle cx="12" cy="16.8" r=".5" /></>,
  box: <><path d="M21 8l-9-4.5L3 8v8l9 4.5L21 16z" /><path d="M3 8l9 4.5L21 8M12 12.5V20.5" /></>,
  doc: <><path d="M13.5 3.5H7a2 2 0 00-2 2v13a2 2 0 002 2h10a2 2 0 002-2V9z" /><path d="M13.5 3.5V9H19" /></>,
  cert: <><circle cx="12" cy="9" r="5" /><path d="M9 13.5L7.5 21l4.5-2.5 4.5 2.5L15 13.5" /></>,
  cal: <><rect x="4" y="5.5" width="16" height="15" rx="2" /><path d="M4 10.5h16M8.5 3.5v3.6M15.5 3.5v3.6" /></>,
}
function Ico({ name, color, size = 40 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color + '26', color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{ICONS[name]}</svg>
    </div>
  )
}
function Kpi({ icon, color, label, value, sub, dot }) {
  return (
    <div className="hcf-card" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '16px 18px' }}>
      <Ico name={icon} color={color} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: C.muted }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
        {sub && <div className="hcf-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />}<span className="hcf-ell">{sub}</span></div>}
      </div>
    </div>
  )
}
const Pill = ({ color, children }) => <span className="hcf-pill" style={{ background: color + '22', color }}><i style={{ background: color }} />{children}</span>
// the Dashboard's readiness ring, small (fleet-1): same score (src/lib/readiness.js), same green / amber / red
function Ring({ score, tone }) {
  const R = 15.5, L = 2 * Math.PI * R, col = TONE_COLOR[tone] || C.faint
  return (
    <span className="hcf-ring" role="img" aria-label={score == null ? 'Readiness: checking' : 'Readiness ' + score + ' of 100'}>
      <svg width="38" height="38" viewBox="0 0 38 38"><circle cx="19" cy="19" r={R} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="4" />
        {score != null && <circle cx="19" cy="19" r={R} fill="none" stroke={col} strokeWidth="4" strokeLinecap="round" strokeDasharray={L} strokeDashoffset={L * (1 - score / 100)} transform="rotate(-90 19 19)" />}</svg>
      <b style={{ color: score == null ? C.faint : C.text }}>{score == null ? '\u2026' : score}</b>
    </span>
  )
}
// fleet-4: the readiness trend of one vessel - the 30-day sparkline and the change vs 7 and 30 days ago (src/lib/readinessTrend.js)
function Trend({ t }) {
  if (!t) return null
  const a7 = arrowOf(t.d7), a30 = arrowOf(t.d30), d = sparkPath(t.spark, 56, 16)
  const say = (x, n) => (x ? (x.delta > 0 ? '+' : '') + x.delta + ' vs ' + n + ' days ago (' + x.from + ')' : 'no score from ' + n + ' days ago yet')
  const col = t.d7 && t.d7.delta < 0 ? C.red : t.d7 && t.d7.delta > 0 ? C.green : C.muted
  return (
    <div className="hcf-trend" title={'Readiness trend: ' + say(t.d7, 7) + ' \u00b7 ' + say(t.d30, 30)} aria-label={'Readiness trend: ' + say(t.d7, 7) + ', ' + say(t.d30, 30)}>
      {d ? <svg width="56" height="16" viewBox="0 0 56 16" aria-hidden="true"><path d={d} fill="none" stroke={col} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg> : null}
      <span className={'hcf-tr-chip ' + a7.tone}>{a7.arrow} {a7.text}<span style={{ fontWeight: 600, opacity: .75 }}>&nbsp;7d</span></span>
      <span className={'hcf-tr-chip ' + a30.tone}>{a30.arrow} {a30.text}<span style={{ fontWeight: 600, opacity: .75 }}>&nbsp;30d</span></span>
    </div>
  )
}
const q = p => Promise.resolve(p).then(r => (r && !r.error && r.data) || [], () => [])

// Renders a list flat (one vessel selected) or grouped by vessel with a short preview per vessel (all vessels).
function VesselList({ items, fleet, grouped, onPickVessel, renderRow, empty }) {
  if (!items.length) return <div style={{ color: C.muted, fontSize: 12.5, padding: '14px 8px' }}>{empty}</div>
  if (!grouped) return <>{items.slice(0, 100).map((it, i) => renderRow(it, i))}</>
  return (
    <>
      {fleet.map(v => {
        const mine = items.filter(it => String(it.vid) === String(v.id))
        if (!mine.length) return null
        return (
          <div key={v.id}>
            <div className="hcf-gh" onClick={() => onPickVessel(v.id)}>
              <span>{v.vessel_name}</span><em>{mine.length}</em>
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: C.muted }}>View &rsaquo;</span>
            </div>
            {mine.slice(0, GROUP_PREVIEW).map((it, i) => renderRow(it, v.id + '-' + i))}
            {mine.length > GROUP_PREVIEW && <span className="hcf-more" onClick={() => onPickVessel(v.id)}>+ {mine.length - GROUP_PREVIEW} more on {v.vessel_name}</span>}
          </div>
        )
      })}
    </>
  )
}

export default function Fleet({ onOpenVessel }) {
  const { config, rawConfig } = useAuth()
  const fleet = config?.fleet || []
  // a company team member (fleet-4): their vessels (the database gives only those) and their areas; the main login: everything
  const member = rawConfig?.member || null
  const rights = member ? cleanRights(member.rights) : null
  const mayApprove = !member || rights.approvals === 'full'
  const [snaps, setSnaps] = useState(null)   // readiness_snapshots of the fleet, last 36 days (null = not read yet / no table)
  const [compliance, setCompliance] = useState({})
  const [positions, setPositions] = useState({})
  const [hover, setHover] = useState(null)
  const [sel, setSel] = useState(null) // null = all vessels, else vessel id
  const [tab, setTab] = useState('overview')
  // Getting started "Show me" opens a fleet tab
  useEffect(() => { const h = ev => { const t = ev && ev.detail && ev.detail.tab; if (t) setTab(t) }; window.addEventListener('hc-fleet-tab', h); return () => window.removeEventListener('hc-fleet-tab', h) }, [])
  const [, setTick] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60 * 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function run() {
      for (const v of fleet) {
        try {
          // that vessel's plan decides what counts, as on its own Dashboard (a management login has Full on every module)
          const see = m => featureOn(rawConfig || config, v, m) && memberCan(member, m)
          const [tasks, docs, crewDocs, eq, inv, drillRecs, ras, ism, sops, crew] = await Promise.all([
            fetchRows('mtask', v.id).catch(() => []),
            fetchRows('docs', v.id).catch(() => []),
            fetchRows('crew_docs', v.id).catch(() => []),
            fetchRows('equipment', v.id).catch(() => []),
            fetchRows('inventory', v.id).catch(() => []),
            see('checklists') ? q(supabase.from('drill_records').select('drill_type, drill_date').eq('client_id', v.id)) : [],
            see('risk') ? q(supabase.from('risk_assessments').select('ra_number,activity,status,next_review_due').eq('client_id', v.id).limit(1000)) : [],
            see('ism_audits') ? q(supabase.from('ism_findings').select('id,ref,grade,status,element,description,due_date').eq('client_id', v.id).neq('status', 'closed')) : [],
            // fleet-2 Compliance tab: SOP reviews, and the crew names for the crew certificates
            see('manuals') ? q(supabase.from('manuals').select('id,title,doc_number,review_due,status,archived_at').eq('client_id', v.id).eq('doc_kind', 'sop').not('review_due', 'is', null)) : [],
            see('crew') ? fetchCrewDb(v.id, 'id,first_name,last_name,name').catch(() => []) : [],
          ])
          const drills = drillsNeedingAttention(drillRecs)
          const vToday = v.timezone ? expiryToday(v.timezone) : localToday()   // that vessel's own date (ship's time zone)
          const health = vesselHealth({ tasks, docs, crewDocs, eq, inv, drills, ras, ismMajor: ism.filter(x => x.grade === 'major_nc').length }, { today: vToday, see })
          const st = r => getVal(r['Status'])
          const overdueL = tasks.filter(t => st(t) === 'Overdue').map(r => ({ name: nameOf(r), date: dateOf(r, /due|next/i) }))
          // expired: the shared rule on the expiry date (src/lib/expiry.js), as the vessel's own Dashboard and lists - never the stored Status
          const expired = r => expiryStatus(r['Expiry Date'], vToday).expired
          const docsL = docs.filter(expired).map(r => ({ name: nameOf(r), date: dateOf(r, /expir/i) }))
          const certsL = crewDocs.filter(expired).map(r => ({ name: nameOf(r), date: dateOf(r, /expir/i) }))
          const oosL = eq.filter(e => st(e) === 'Out of Service').map(r => ({ name: nameOf(r) }))
          const oostock = inv.filter(i => st(i) === 'Out of Stock').length
          const total = overdueL.length + docsL.length + certsL.length + oosL.length + oostock
          const due30 = health.due.filter(x => x.window === 30).length
          const drillsLateL = drills.filter(x => x.status === 'overdue').map(x => ({ name: x.label + ' drill' }))
          const raLateL = health.raLate.map(r => ({ name: [r.ra_number, r.activity].filter(Boolean).join(' ') || 'Risk assessment', date: r.next_review_due }))
          if (!cancelled) setCompliance(c => ({ ...c, [v.id]: { total, overdue: overdueL.length, expiredDocs: docsL.length, expiredCerts: certsL.length, oos: oosL.length, oostock, overdueL, docsL, certsL, oosL, health, due30, dueL: health.due, drillsLateL, raLateL, raw: { docs, crewDocs, crew, drills, ras, ism, sops } } }))
        } catch (e) { /* skip vessel on error */ }
      }
    }
    if (fleet.length) run()
    return () => { cancelled = true }
  }, [fleet.map(v => v.id).join(',')]) // eslint-disable-line

  // fleet-4: the daily readiness snapshots (written by the 05:00 job) for the 7 / 30-day trend
  useEffect(() => {
    let cancelled = false
    const ids = fleet.map(v => v.id)
    if (!ids.length) return
    const since = new Date(Date.now() - 36 * 864e5).toISOString().slice(0, 10)
    supabase.from('readiness_snapshots').select('client_id,day,score').in('client_id', ids).gte('day', since).order('day').limit(5000)
      .then(({ data, error }) => { if (!cancelled) setSnaps(error ? null : data || []) }, () => {})
    return () => { cancelled = true }
  }, [fleet.map(v => v.id).join(',')]) // eslint-disable-line

  useEffect(() => {
    let cancelled = false
    const mmsis = fleet.map(v => v.mmsi).filter(Boolean)
    if (!mmsis.length) return
    const mmsiParam = mmsis.join(',')
    async function readCache() {
      try {
        const res = await fetch('/api/workspace?cached=1&mmsi=' + encodeURIComponent(mmsiParam))
        const data = await res.json()
        if (!cancelled && data.positions) setPositions(data.positions)
      } catch (e) {}
    }
    async function collect() {
      try { await fetch('/api/workspace?all=1&mmsi=' + encodeURIComponent(mmsiParam)); if (!cancelled) readCache() } catch (e) {}
    }
    readCache()
    collect()
    const id = setInterval(collect, 5 * 60 * 1000)
    return () => { cancelled = true; clearInterval(id) }
  }, [fleet.map(v => v.id).join(',')]) // eslint-disable-line

  const same = (a, b) => String(a) === String(b)
  const scope = sel == null ? fleet : fleet.filter(v => same(v.id, sel))
  const grouped = sel == null && fleet.length > 1
  const selVessel = sel == null ? null : fleet.find(v => same(v.id, sel))
  const pick = id => setSel(cur => (same(cur, id) ? null : id))

  const posOf = v => (v.mmsi ? positions[String(v.mmsi)] : null)
  const mapVessels = fleet.filter(v => posOf(v)).map(v => {
    const p = posOf(v)
    return { id: v.id, vessel_name: v.vessel_name, lat: p.lat, lon: p.lon, speed: p.speed, course: p.course, ts: p.ts, total: compliance[v.id]?.total, score: compliance[v.id]?.health?.score, tone: compliance[v.id]?.health?.tone }
  })
  const scopeMap = mapVessels.filter(m => scope.some(v => same(v.id, m.id)))
  const allLoaded = scope.length > 0 && scope.every(v => compliance[v.id])
  const sum = k => scope.reduce((s, v) => s + (compliance[v.id]?.[k] || 0), 0)
  const underway = scopeMap.filter(v => Number(v.speed || 0) > 0.5).length
  const dash = '\u2014'
  const openById = id => { const v = fleet.find(x => same(x.id, id)); if (v && onOpenVessel) onOpenVessel(v) }
  // approval card: open that vessel straight on the PO / APA
  const openItem = r => { const v = fleet.find(x => same(x.id, r.client_id)); if (v && onOpenVessel) onOpenVessel(v, rememberOpen(r.kind, r.open_id, r.title)) }
  const onMarker = id => {
    pick(id)
    const el = document.getElementById('fleet-row-' + id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const collect = key => scope.flatMap(v => (compliance[v.id]?.[key] || []).map(it => ({ ...it, vid: v.id })))
  const overdueAll = collect('overdueL').sort((a, b) => tsOf(a.date) - tsOf(b.date))
  const expiredAll = [...collect('docsL').map(x => ({ ...x, kind: 'doc' })), ...collect('certsL').map(x => ({ ...x, kind: 'cert' }))].sort((a, b) => tsOf(a.date) - tsOf(b.date))
  // red first (out of service, expired, overdue drills / RA reviews), then what falls due in 30 / 60 / 90 days, in amber (fleet-1)
  const DUE_ICON = { doc: 'doc', survey: 'cal', crew: 'cert', drill: 'alert', ra: 'doc' }
  const alerts = [
    ...collect('oosL').map(x => ({ icon: 'wrench', color: C.amber, title: 'Out of service: ' + x.name, vid: x.vid })),
    ...collect('docsL').map(x => ({ icon: 'doc', color: C.red, title: 'Expired: ' + x.name, vid: x.vid, date: x.date })),
    ...collect('certsL').map(x => ({ icon: 'cert', color: C.purple, title: 'Crew cert expired: ' + x.name, vid: x.vid, date: x.date })),
    ...collect('drillsLateL').map(x => ({ icon: 'alert', color: C.red, title: 'Overdue: ' + x.name, vid: x.vid })),
    ...collect('raLateL').map(x => ({ icon: 'doc', color: C.red, title: 'RA review overdue: ' + x.name, vid: x.vid, date: x.date })),
    // drills due this month: one line per vessel ("3 drills due this month"), not one per drill
    ...scope.flatMap(v => { const n = (compliance[v.id]?.dueL || []).filter(x => x.kind === 'drill'); return n.length > 1 ? [{ icon: 'alert', color: C.amber, title: n.length + ' drills due this month: ' + n.map(x => x.name.replace(/ drill$/, '')).join(', '), vid: v.id, win: 30 }] : [] }),
    ...collect('dueL').filter(x => x.kind !== 'drill' || (compliance[x.vid]?.dueL || []).filter(y => y.kind === 'drill').length === 1)
      .sort((a, b) => a.days - b.days).map(x => ({ icon: DUE_ICON[x.kind] || 'cal', color: C.amber, title: x.label, vid: x.vid, date: x.date, win: x.window, soft: x.window > 30 })),
  ]
  const due60 = scope.reduce((s, v) => s + (compliance[v.id]?.dueL || []).filter(x => x.window === 60).length, 0)
  const due90 = scope.reduce((s, v) => s + (compliance[v.id]?.dueL || []).filter(x => x.window === 90).length, 0)
  const health = Object.fromEntries(fleet.filter(v => compliance[v.id]).map(v => [v.id, compliance[v.id].health]))
  const ranked = worstFirst(fleet, health)
  // fleet-2 Compliance tab + fleet report: every item overdue / due in 90 days, per vessel (that vessel's plan decides what counts)
  const seeOf = v => m => featureOn(rawConfig || config, v, m) && memberCan(member, m)
  const itemsOf = v => (compliance[v.id] && compliance[v.id].raw ? vesselCompliance(v, compliance[v.id].raw, { today: localToday(), see: seeOf(v) }) : [])
  const itemsFor = id => { const v = fleet.find(x => same(x.id, id)); return v ? itemsOf(v) : [] }
  const fleetLoaded = fleet.length > 0 && fleet.every(v => compliance[v.id])
  const complianceItems = tab === 'compliance' ? scope.flatMap(itemsOf) : []
  const trendOf = v => (snaps ? trendFrom(snaps.filter(x => String(x.client_id) === String(v.id)), { today: localToday(), live: compliance[v.id]?.health?.score ?? null }) : null)
  // the tabs this login has: Compliance / Standards by the member's areas; Crew Logins and Team for the main login only
  const TABS = [['overview', 'Overview'], ['compliance', 'Compliance'], ['standards', 'Standards'], ['calendar', 'Calendar'], ['crew', 'Crew Logins'], ['team', 'Team']]
    .filter(([k]) => !member ? true : k === 'compliance' ? memberCan(member, 'documents') : k === 'standards' ? rights.standards !== 'none' : !['crew', 'team'].includes(k))
  const showReport = !member || memberCan(member, 'reports')
  const briefing = () => window.dispatchEvent(new CustomEvent('hc-assistant-open', { detail: { text: 'Fleet briefing' } }))

  const hour = new Date().getHours()
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  const loadingNote = <div style={{ color: C.muted, fontSize: 12.5, padding: '14px 8px' }}>Checking&hellip;</div>
  const cols3 = 'minmax(0,1fr) 110px auto'
  const OV = 'minmax(0,1fr) 44px 44px 36px 30px'

  return (
    <div className="hcf">
      <style>{CSS}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-.01em' }}>{greet}</div>
          <div style={{ fontSize: 13.5, color: C.muted, marginTop: 3 }}>{member ? member.name + (member.title ? ', ' + member.title : '') + ' \u00b7 ' : ''}{config?.companyName || 'Your fleet'} &middot; here is what is happening across {member && member.vessel_ids ? 'your vessels' : 'your fleet'} today.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, color: C.muted, fontSize: 13, flexWrap: 'wrap' }}>
          <div data-tour="fleet-tabs" style={{ display: 'inline-flex', background: '#0c1627', border: '1px solid ' + C.line, borderRadius: 10, padding: 3 }}>
            {TABS.map(([k, l]) => (
              <button key={k} onClick={() => { setTab(k); if (k === 'compliance') tutorialMark('compliance') }} style={{ background: tab === k ? C.blue : 'transparent', color: tab === k ? '#fff' : C.muted, border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{l}</button>
            ))}
          </div>
          {fleet.length > 0 && showReport && <span data-tour="fleet-report" onClickCapture={() => tutorialMark('fleet_report')}><FleetReportButton fleet={fleet} company={config?.companyName || ''} health={health} itemsFor={itemsFor} ready={fleetLoaded} palette={C} /></span>}
          {fleet.length > 0 && <button className="hcf-brief" onClick={briefing} title="The AI assistant: one line per vessel that needs you"><span aria-hidden="true">{'\u2693'}</span>Fleet briefing</button>}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{ICONS.cal}</svg>{today}</span>
        </div>
      </div>

      {fleet.length === 0 ? (
        <div className="hcf-card" style={{ color: C.muted, fontSize: 14, padding: 40, textAlign: 'center' }}>No vessels assigned to this company yet.</div>
      ) : (
        <>
          {fleet.length > 1 && (
            <div className="hcf-chips" data-tour="fleet-vessels">
              <button className={'hcf-chip' + (sel == null ? ' on' : '')} onClick={() => setSel(null)}>All vessels <em>{fleet.length}</em></button>
              {fleet.map(v => (
                <button key={v.id} className={'hcf-chip' + (same(sel, v.id) ? ' on' : '')} onClick={() => pick(v.id)}>
                  {v.vessel_name}{compliance[v.id] && compliance[v.id].total > 0 && <em>{compliance[v.id].total}</em>}
                </button>
              ))}
              {selVessel && (
                <button className="hcf-chip" style={{ marginLeft: 'auto', borderColor: C.blue, color: '#9bbcff' }} onClick={() => openById(selVessel.id)}>Open {selVessel.vessel_name} &rarr;</button>
              )}
            </div>
          )}

          {tab === 'overview' ? (<>
          <div className="hcf-kpis">
            <Kpi icon="ship" color={C.blue} label={selVessel ? 'Vessel' : 'Total vessels'} value={selVessel ? 1 : fleet.length} sub={selVessel ? selVessel.vessel_name : 'Active'} dot={C.green} />
            <Kpi icon="pin" color={C.green} label="Underway" value={scopeMap.length ? underway : dash} sub={scopeMap.length ? (scopeMap.length - underway) + ' stationary' : 'Locating\u2026'} dot={C.blue} />
            <Kpi icon="wrench" color={C.amber} label="Overdue maintenance" value={allLoaded ? sum('overdue') : dash} sub="Tasks past due" dot={C.amber} />
            <Kpi icon="alert" color={C.red} label="Expired documents" value={allLoaded ? sum('expiredDocs') + sum('expiredCerts') : dash} sub={allLoaded ? sum('expiredDocs') + ' ship \u00b7 ' + sum('expiredCerts') + ' crew certs' : 'Checking\u2026'} dot={C.red} />
            <Kpi icon="cal" color={C.amber} label="Due in 30 days" value={allLoaded ? sum('due30') : dash} sub={allLoaded ? due60 + ' in 60 d \u00b7 ' + due90 + ' in 90 d' : 'Checking\u2026'} dot={C.amber} />
            <Kpi icon="box" color={C.purple} label="Out of service" value={allLoaded ? sum('oos') : dash} sub={allLoaded ? sum('oostock') + ' items out of stock' : 'Checking\u2026'} dot={C.purple} />
          </div>

          {mayApprove && <ApprovalsCard palette={{ card: C.card, line: C.line, text: C.text, muted: C.muted, accent: C.blue, red: C.red, amber: C.amber, bad: '#ff8a8d', good: '#35c98b' }} scopeVesselId={sel} onOpen={openItem} actions
            title={'Waiting for your approval' + (selVessel ? ' · ' + selVessel.vessel_name : '')} />}

          <div className="hcf-row2">
            <div className="hcf-card" style={{ padding: 14 }}>
              <div className="hcf-h" style={{ padding: '2px 4px 0' }}><b>Fleet Location</b><span>AIS &middot; last stored positions, collected hourly and every 5 min while open</span></div>
              <FleetMap vessels={mapVessels} highlightId={hover ?? sel} focusId={sel} onMarkerClick={onMarker} onOpen={openById} height={380} />
            </div>

            <div className="hcf-card">
              <div className="hcf-h"><b>Fleet Overview</b><span>Readiness, worst first &middot; trend 7 / 30 days &middot; click to open</span></div>
              <div className="hcf-th hcf-th-ov" style={{ gridTemplateColumns: OV }}>
                <span>Vessel</span><span style={{ textAlign: 'center' }} title="Overdue"><i className="hcf-long">Overdue</i><i className="hcf-short">Late</i></span><span style={{ textAlign: 'center' }} title="Expired"><i className="hcf-long">Expired</i><i className="hcf-short">Exp</i></span><span style={{ textAlign: 'center' }} title="Out of service">OOS</span><span />
              </div>
              <div className="hcf-scroll hcf-all">
                {ranked.map(v => {
                  const p = posOf(v)
                  const c = compliance[v.id]
                  const h = c && c.health
                  const edge = h ? TONE_COLOR[h.tone] : C.faint
                  const moving = p && Number(p.speed || 0) > 0.5
                  const n = (x, col) => <div className="hcf-num" style={{ color: !c ? C.faint : x > 0 ? col : C.green }}>{c ? x : dash}</div>
                  return (
                    <div key={v.id} id={'fleet-row-' + v.id} data-score={h ? h.score : ''}
                      className={'hcf-tr hcf-ov' + (same(sel, v.id) ? ' sel' : same(hover, v.id) ? ' hl' : '')}
                      style={{ gridTemplateColumns: OV, boxShadow: 'inset 3px 0 0 ' + edge }}
                      title={'Open ' + v.vessel_name}
                      onMouseEnter={() => setHover(v.id)} onMouseLeave={() => setHover(null)} onClick={() => openById(v.id)}>
                      <div className="hcf-vc">
                        <Ring score={h ? h.score : null} tone={h && h.tone} />
                        <div style={{ minWidth: 0 }}>
                          <div className="hcf-vn">{v.vessel_name}</div>
                          <div className="hcf-sub hcf-ell">
                            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', marginRight: 5, verticalAlign: 'middle', background: !p ? C.faint : moving ? C.green : C.blue }} />
                            {!v.mmsi ? 'No MMSI' : !p ? 'Locating\u2026' : (moving ? 'Underway ' + Number(p.speed).toFixed(1) + ' kn' : 'Stationary') + ' \u00b7 ' + fmtLat(p.lat) + ' ' + fmtLon(p.lon)}
                          </div>
                          <Trend t={trendOf(v)} />
                        </div>
                      </div>
                      {n(c?.overdue, C.amber)}
                      {n((c?.expiredDocs || 0) + (c?.expiredCerts || 0), C.red)}
                      {n(c?.oos, C.purple)}
                      <button className="hcf-open" title={'Open ' + v.vessel_name} onClick={e => { e.stopPropagation(); openById(v.id) }}>&rarr;</button>
                      <div className="hcf-why" style={{ color: h ? (h.tone === 'good' ? C.muted : edge) : C.faint }}>{h ? h.reason : 'Checking\u2026'}</div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="hcf-card">
              <div className="hcf-h"><b>Alerts</b><span>{allLoaded ? alerts.length + (selVessel ? ' \u00b7 ' + selVessel.vessel_name : '') : ''}</span></div>
              <div className="hcf-scroll">
                {!allLoaded ? loadingNote : (
                  <VesselList items={alerts} fleet={ranked} grouped={grouped} onPickVessel={pick} empty="No alerts. All clear."
                    renderRow={(a, k) => (
                      <div key={k} className="hcf-tr" style={{ gridTemplateColumns: 'auto minmax(0,1fr)' }} onClick={() => openById(a.vid)}>
                        <Ico name={a.icon} color={a.color} size={30} />
                        <div style={{ minWidth: 0 }}>
                          <div className="hcf-ell" style={{ fontSize: 12.5, fontWeight: 600, color: a.win ? (a.soft ? '#e8c27a' : C.amber) : undefined }}>{a.title}</div>
                          {(a.date || a.win) && <div className="hcf-sub hcf-ell">{a.date ? fmtDate(a.date) : 'This month'}{a.win && <span className="hcf-win" style={{ background: C.amber + (a.soft ? '1a' : '2e'), color: a.soft ? '#e8c27a' : C.amber }}>{'\u2264 ' + a.win + ' d'}</span>}</div>}
                        </div>
                      </div>
                    )} />
                )}
              </div>
            </div>
          </div>

          <div className="hcf-row3">
            <div className="hcf-card">
              <div className="hcf-h"><b>Overdue Maintenance</b><span>{allLoaded ? overdueAll.length + ' tasks' + (selVessel ? ' \u00b7 ' + selVessel.vessel_name : '') : ''}</span></div>
              <div className="hcf-th" style={{ gridTemplateColumns: cols3 }}><span>Task</span><span>Due</span><span>Status</span></div>
              <div className="hcf-scroll">
                {!allLoaded ? loadingNote : (
                  <VesselList items={overdueAll} fleet={ranked} grouped={grouped} onPickVessel={pick} empty="Nothing overdue."
                    renderRow={(t, k) => (
                      <div key={k} className="hcf-tr" style={{ gridTemplateColumns: cols3 }} onClick={() => openById(t.vid)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          <Ico name="wrench" color={C.amber} size={30} />
                          <div className="hcf-ell" style={{ fontSize: 12.5, fontWeight: 600 }}>{t.name}</div>
                        </div>
                        <div style={{ fontSize: 12, color: C.muted }}>{fmtDate(t.date) || dash}</div>
                        <Pill color={C.red}>Overdue</Pill>
                      </div>
                    )} />
                )}
              </div>
            </div>

            <div className="hcf-card">
              <div className="hcf-h"><b>Expired Documents &amp; Certificates</b><span>{allLoaded ? expiredAll.length + ' items' + (selVessel ? ' \u00b7 ' + selVessel.vessel_name : '') : ''}</span></div>
              <div className="hcf-th" style={{ gridTemplateColumns: cols3 }}><span>Document</span><span>Expired</span><span>Type</span></div>
              <div className="hcf-scroll">
                {!allLoaded ? loadingNote : (
                  <VesselList items={expiredAll} fleet={ranked} grouped={grouped} onPickVessel={pick} empty="Nothing expired."
                    renderRow={(d, k) => (
                      <div key={k} className="hcf-tr" style={{ gridTemplateColumns: cols3 }} onClick={() => openById(d.vid)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          <Ico name={d.kind === 'cert' ? 'cert' : 'doc'} color={d.kind === 'cert' ? C.purple : C.red} size={30} />
                          <div className="hcf-ell" style={{ fontSize: 12.5, fontWeight: 600 }}>{d.name}</div>
                        </div>
                        <div style={{ fontSize: 12, color: C.muted }}>{fmtDate(d.date) || dash}</div>
                        {d.kind === 'cert' ? <Pill color={C.purple}>Crew cert</Pill> : <Pill color={C.red}>Ship doc</Pill>}
                      </div>
                    )} />
                )}
              </div>
            </div>
          </div>
          </>) : (
            tab === 'compliance'
              ? <FleetCompliance items={complianceItems} fleet={scope} health={health} company={config?.companyName || ''} palette={C} onOpen={openById} loading={!allLoaded} />
              : tab === 'standards'
              ? <FleetStandards fleet={scope} palette={C} onOpenVessel={onOpenVessel} />
              : tab === 'calendar'
              ? <FleetCalendar fleet={fleet} scope={scope} onPickVessel={pick} onOpen={openById} />
              : tab === 'team' && !member
              ? <FleetTeam fleet={fleet} palette={C} />
              : tab === 'crew' && !member
              ? <CrewLogins vessels={scope} />
              : null
          )}
        </>
      )}
    </div>
  )
}
